import { useEffect, useRef, useState } from 'react';
import { Play, Loader2, CheckCircle2, XCircle, Trash2, ExternalLink, ChevronDown, ChevronUp, MousePointer, Type, Navigation, ArrowDown, Clock, Brain } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ── Types ──

interface CrawlerScreenshot {
  step: number;
  label: string;
  timestamp: string;
  pageUrl: string;
}

interface StepMeta {
  step: number;
  url: string;
  status: 'thinking' | 'acting' | 'done';
  thinking: string;
  actions: Array<{ action: string; description?: string }>;
}

interface CrawlResult {
  source: string;
  name: string;
  description: string;
  url: string;
  date?: string;
  metadata?: Record<string, unknown>;
}

interface JobResponse {
  id: string;
  status: 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  completedAt?: string;
  crawlers: Record<string, { status: string; resultCount: number; durationMs?: number; error?: string }>;
  resultCount: number;
  results: CrawlResult[];
}

// ── Helpers ──

const CRAWLERS_BASE = '/crawlers-api';

function parseLabel(label: string): StepMeta | null {
  try {
    const parsed = JSON.parse(label);
    if (parsed && typeof parsed.step === 'number') return parsed as StepMeta;
  } catch { /* not JSON, old-style label */ }
  return null;
}

const ACTION_ICONS: Record<string, typeof MousePointer> = {
  click: MousePointer,
  type: Type,
  navigate: Navigation,
  scroll: ArrowDown,
  wait: Clock,
  done: CheckCircle2,
};

// ── Main Page ──

export function CrawlersPage() {
  const [url, setUrl] = useState('');
  const [prompt, setPrompt] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<string | null>(null);
  const [screenshots, setScreenshots] = useState<CrawlerScreenshot[]>([]);
  const [result, setResult] = useState<CrawlResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<Array<{
    id: string;
    url: string;
    prompt: string;
    result: CrawlResult;
    screenshots: CrawlerScreenshot[];
    startedAt: string;
    completedAt: string;
  }>>([]);
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);
  const [enlargedImg, setEnlargedImg] = useState<{ src: string; label: string; url?: string } | null>(null);
  const [expandedStep, setExpandedStep] = useState<number | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const screenshotPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevCount = useRef(0);

  // Auto-scroll and auto-expand latest step on new screenshots
  useEffect(() => {
    if (screenshots.length > prevCount.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      // Auto-expand the latest step
      const deduped = deduplicateScreenshots(screenshots);
      if (deduped.length > 0) {
        setExpandedStep(deduped[deduped.length - 1].step);
      }
    }
    prevCount.current = screenshots.length;
  }, [screenshots.length]);

  // Poll screenshots while running
  useEffect(() => {
    if (!isRunning) {
      if (screenshotPollRef.current) clearInterval(screenshotPollRef.current);
      return;
    }
    const poll = async () => {
      try {
        const res = await fetch(`${CRAWLERS_BASE}/crawlers/web-crawler/screenshots`);
        if (!res.ok) return;
        const data = await res.json();
        if (data?.screenshots) setScreenshots(data.screenshots);
      } catch { /* ignore */ }
    };
    poll();
    screenshotPollRef.current = setInterval(poll, 2000);
    return () => { if (screenshotPollRef.current) clearInterval(screenshotPollRef.current); };
  }, [isRunning]);

  // Poll job status
  useEffect(() => {
    if (!jobId || !isRunning) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await fetch(`${CRAWLERS_BASE}/jobs/${jobId}`);
        if (!res.ok) return;
        const job: JobResponse = await res.json();
        setJobStatus(job.status);
        if (job.status !== 'running') {
          setIsRunning(false);
          if (pollRef.current) clearInterval(pollRef.current);
          if (job.status === 'completed' && job.results.length > 0) {
            setResult(job.results[0]);
            const ssRes = await fetch(`${CRAWLERS_BASE}/crawlers/web-crawler/screenshots`);
            if (ssRes.ok) {
              const ssData = await ssRes.json();
              const finalSS = ssData?.screenshots || [];
              setScreenshots(finalSS);
              setHistory(prev => [{
                id: jobId, url: `https://${url}`, prompt,
                result: job.results[0], screenshots: finalSS,
                startedAt: job.startedAt, completedAt: job.completedAt || new Date().toISOString(),
              }, ...prev].slice(0, 20));
            }
            toast.success('Crawl completed');
          } else if (job.status === 'failed') {
            const detail = job.crawlers['web-crawler'];
            setError(detail?.error || 'Crawl failed');
            toast.error('Crawl failed');
          }
        }
      } catch { /* ignore */ }
    }, 1500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobId, isRunning, url, prompt]);

  const handleRun = async () => {
    if (!url.trim() || !prompt.trim()) { toast.error('URL and prompt are required'); return; }
    setIsRunning(true); setJobId(null); setJobStatus('starting');
    setScreenshots([]); setResult(null); setError(null); prevCount.current = 0;
    try {
      const res = await fetch(`${CRAWLERS_BASE}/crawlers/web-crawler/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: `https://${url.trim()}`, prompt: prompt.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setJobId(data.jobId); setJobStatus('running');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      toast.error('Failed to start crawl', { description: msg });
      setIsRunning(false); setError(msg); setJobStatus('failed');
    }
  };

  const handleClear = () => {
    setUrl(''); setPrompt(''); setScreenshots([]); setResult(null);
    setError(null); setJobId(null); setJobStatus(null);
  };

  // Deduplicate screenshots — keep last per step number (the one with reasoning)
  const deduped = deduplicateScreenshots(screenshots);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Web Crawler</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Enter a URL and a prompt. The AI agent will navigate the website autonomously to find what you need.
        </p>
      </div>

      {/* Input form */}
      <div className="rounded-xl border bg-card p-5 space-y-4">
        <div className="flex gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Website URL</label>
            <div className="flex">
              <span className="inline-flex items-center rounded-l-md border border-r-0 border-input bg-muted px-3 text-sm text-muted-foreground select-none">
                https://
              </span>
              <Input
                placeholder="example.com"
                value={url}
                onChange={(e) => setUrl(e.target.value.replace(/^https?:\/\//, ''))}
                disabled={isRunning}
                className="rounded-l-none"
              />
            </div>
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Prompt</label>
          <textarea
            placeholder="e.g. Find the pricing page and extract all plan names and prices as a table"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            disabled={isRunning}
            rows={3}
            className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={handleRun} disabled={isRunning || !url.trim() || !prompt.trim()}>
            {isRunning ? (
              <><Loader2 className="h-4 w-4 animate-spin mr-2" />Crawling...</>
            ) : (
              <><Play className="h-4 w-4 mr-2" />Run Crawler</>
            )}
          </Button>
          {(result || error || screenshots.length > 0) && !isRunning && (
            <Button variant="outline" onClick={handleClear}>
              <Trash2 className="h-4 w-4 mr-2" />Clear
            </Button>
          )}
        </div>
      </div>

      {/* Live progress — vertical timeline */}
      {(isRunning || deduped.length > 0) && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold">
              {isRunning ? 'Agent Progress' : 'Navigation Steps'}
            </h2>
            {isRunning && (
              <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 animate-pulse">
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
                Step {deduped.length}
              </Badge>
            )}
            {jobStatus === 'completed' && (
              <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Done in {deduped.length} steps
              </Badge>
            )}
            {jobStatus === 'failed' && (
              <Badge variant="outline" className="text-red-600 border-red-200 bg-red-50">
                <XCircle className="h-3 w-3 mr-1" />Failed
              </Badge>
            )}
          </div>

          {/* Timeline */}
          <div className="space-y-2">
            {deduped.map((ss, i) => (
              <StepRow
                key={`${ss.step}-${i}`}
                screenshot={ss}
                index={i}
                isLatest={isRunning && i === deduped.length - 1}
                expanded={expandedStep === ss.step}
                onToggle={() => setExpandedStep(expandedStep === ss.step ? null : ss.step)}
                onEnlarge={(src, label, url) => setEnlargedImg({ src, label, url })}
              />
            ))}

            {/* Thinking placeholder */}
            {isRunning && (
              <div className="flex items-center justify-center rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/30 p-6">
                <Loader2 className="h-5 w-5 animate-spin text-blue-400 mr-2" />
                <span className="text-sm text-blue-600 font-medium">AI is navigating...</span>
              </div>
            )}
          </div>
          <div ref={bottomRef} />
        </div>
      )}

      {/* Result — preview on left, AI answer on right */}
      {result && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="flex flex-col lg:flex-row">
            {/* Left — last screenshot as preview */}
            {deduped.length > 0 && (
              <div className="shrink-0 lg:w-[360px] border-b lg:border-b-0 lg:border-r">
                <div
                  className="relative bg-muted cursor-pointer hover:opacity-90 transition-opacity"
                  onClick={() => {
                    const last = deduped[deduped.length - 1];
                    setEnlargedImg({
                      src: `${CRAWLERS_BASE}/crawlers/web-crawler/screenshots/${last.step}`,
                      label: result.name || result.url,
                      url: result.url,
                    });
                  }}
                >
                  <img
                    src={`${CRAWLERS_BASE}/crawlers/web-crawler/screenshots/${deduped[deduped.length - 1].step}`}
                    alt="Final page"
                    className="w-full h-auto object-contain max-h-[300px]"
                    loading="lazy"
                  />
                </div>
                <div className="px-3 py-2 border-t bg-muted/30">
                  {result.name && result.name !== result.url && (
                    <p className="text-xs font-medium truncate">{result.name}</p>
                  )}
                  <a href={result.url} target="_blank" rel="noopener noreferrer"
                    className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1 truncate">
                    {result.url} <ExternalLink className="h-2.5 w-2.5 shrink-0" />
                  </a>
                  {result.metadata && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {'model' in result.metadata && (
                        <Badge variant="secondary" className="text-[10px] py-0">{`${result.metadata.model}`}</Badge>
                      )}
                      {'stepsUsed' in result.metadata && (
                        <Badge variant="secondary" className="text-[10px] py-0">{`${result.metadata.stepsUsed} steps`}</Badge>
                      )}
                      {'cached' in result.metadata && !!(result.metadata as Record<string, unknown>).cached && (
                        <Badge variant="outline" className="text-[10px] py-0 text-amber-600 border-amber-200">cached</Badge>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Right — AI answer */}
            <div className="flex-1 min-w-0 p-5">
              <div className="flex items-center gap-2 mb-3">
                <Brain className="h-4 w-4 text-purple-500" />
                <h2 className="text-sm font-semibold">AI Response</h2>
              </div>
              <div className="prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap text-sm leading-relaxed">
                {result.description}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && !isRunning && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-5">
          <div className="flex items-center gap-2 mb-1">
            <XCircle className="h-4 w-4 text-red-600" />
            <span className="font-medium text-red-800 text-sm">Crawl Failed</span>
          </div>
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold">Recent Crawls</h2>
          <div className="space-y-2">
            {history.map((item) => (
              <div key={item.id} className="rounded-xl border bg-card">
                <button
                  className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors"
                  onClick={() => setExpandedHistory(expandedHistory === item.id ? null : item.id)}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{item.result.name || item.url}</span>
                      <Badge variant="secondary" className="text-xs shrink-0">{item.screenshots.length} steps</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground truncate mt-0.5">{item.prompt}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4 shrink-0">
                    <span className="text-xs text-muted-foreground">{new Date(item.completedAt).toLocaleTimeString()}</span>
                    {expandedHistory === item.id
                      ? <ChevronUp className="h-4 w-4 text-muted-foreground" />
                      : <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    }
                  </div>
                </button>
                {expandedHistory === item.id && (
                  <div className="border-t px-4 py-4 space-y-3">
                    {deduplicateScreenshots(item.screenshots).map((ss, i) => {
                      const meta = parseLabel(ss.label);
                      const imgSrc = `${CRAWLERS_BASE}/crawlers/web-crawler/screenshots/${ss.step}`;
                      return (
                        <div key={`${item.id}-${ss.step}`} className="flex flex-col sm:flex-row gap-3 border-b last:border-0 pb-3 last:pb-0">
                          <div className="shrink-0 sm:w-64">
                            <img
                              src={imgSrc} alt={`Step ${i + 1}`}
                              className="w-full rounded-lg border cursor-pointer hover:shadow-md transition-shadow"
                              onClick={() => setEnlargedImg({ src: imgSrc, label: `Step ${i + 1}`, url: meta?.url })}
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0 text-xs space-y-1">
                            {meta?.thinking && <p className="text-muted-foreground">{meta.thinking}</p>}
                            {meta?.actions.map((a, ai) => (
                              <span key={ai} className="inline-flex items-center gap-1 mr-2">
                                <Badge variant="outline" className="text-[10px] py-0">{a.action}</Badge>
                                {a.description && <span className="text-muted-foreground">{a.description}</span>}
                              </span>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    <div className="bg-muted/30 rounded-lg p-4">
                      <p className="text-sm whitespace-pre-wrap">{item.result.description}</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Enlarged overlay */}
      {enlargedImg && (
        <div
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-8 cursor-pointer"
          onClick={() => setEnlargedImg(null)}
        >
          <div className="relative max-w-[90vw] max-h-[90vh]">
            <img src={enlargedImg.src} alt={enlargedImg.label}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl" />
            <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white px-4 py-2 rounded-b-lg">
              <p className="text-sm font-medium">{enlargedImg.label}</p>
              {enlargedImg.url && <p className="text-xs opacity-70 truncate">{enlargedImg.url}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──

/** Keep only the last screenshot per step number (the one with reasoning attached) */
function deduplicateScreenshots(screenshots: CrawlerScreenshot[]): CrawlerScreenshot[] {
  const byStep = new Map<number, CrawlerScreenshot>();
  for (const ss of screenshots) {
    const meta = parseLabel(ss.label);
    const key = meta?.step ?? ss.step;
    byStep.set(key, ss);
  }
  return Array.from(byStep.values()).sort((a, b) => a.step - b.step);
}

// ── Collapsible Step Row ──

function StepRow({
  screenshot,
  index,
  isLatest,
  expanded,
  onToggle,
  onEnlarge,
}: {
  screenshot: CrawlerScreenshot;
  index: number;
  isLatest: boolean;
  expanded: boolean;
  onToggle: () => void;
  onEnlarge: (src: string, label: string, url?: string) => void;
}) {
  const meta = parseLabel(screenshot.label);
  const imgSrc = `${CRAWLERS_BASE}/crawlers/web-crawler/screenshots/${screenshot.step}`;
  const stepNum = meta?.step || index + 1;
  const thinkingSummary = meta?.thinking
    ? meta.thinking.length > 100 ? meta.thinking.substring(0, 100) + '...' : meta.thinking
    : '';

  return (
    <div className={cn(
      'rounded-lg border bg-card transition-all overflow-hidden',
      isLatest && 'ring-2 ring-blue-400 ring-offset-1',
    )}>
      {/* Collapsed header — always visible */}
      <button
        className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-muted/30 transition-colors"
        onClick={onToggle}
      >
        <Badge className={cn(
          'text-[10px] font-bold shrink-0',
          isLatest ? 'bg-blue-600' : 'bg-black/70',
        )}>
          {stepNum}
        </Badge>

        {/* Thinking summary */}
        <div className="flex-1 min-w-0">
          {meta?.status === 'thinking' && isLatest ? (
            <span className="text-xs text-blue-600 animate-pulse flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" /> Analyzing page...
            </span>
          ) : thinkingSummary ? (
            <p className="text-xs text-muted-foreground truncate">{thinkingSummary}</p>
          ) : (
            <p className="text-xs text-muted-foreground italic">Initial page load</p>
          )}
        </div>

        {/* Action badges */}
        <div className="flex items-center gap-1 shrink-0">
          {meta?.actions.map((a, ai) => {
            const Icon = ACTION_ICONS[a.action] || MousePointer;
            return (
              <div key={ai} className={cn(
                'flex h-5 w-5 items-center justify-center rounded',
                a.action === 'done' ? 'bg-green-100' :
                a.action === 'click' ? 'bg-blue-100' :
                a.action === 'navigate' ? 'bg-amber-100' :
                'bg-muted',
              )}>
                <Icon className={cn(
                  'h-3 w-3',
                  a.action === 'done' ? 'text-green-600' :
                  a.action === 'click' ? 'text-blue-600' :
                  a.action === 'navigate' ? 'text-amber-600' :
                  'text-muted-foreground',
                )} />
              </div>
            );
          })}
          {expanded
            ? <ChevronUp className="h-4 w-4 text-muted-foreground ml-1" />
            : <ChevronDown className="h-4 w-4 text-muted-foreground ml-1" />
          }
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t px-4 py-3 flex flex-col lg:flex-row gap-4">
          {/* Screenshot */}
          <div className="shrink-0 lg:w-[480px]">
            <div
              className="relative bg-muted rounded-lg overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => onEnlarge(imgSrc, `Step ${stepNum}`, meta?.url || screenshot.pageUrl)}
            >
              <img src={imgSrc} alt={`Step ${stepNum}`} className="w-full h-auto object-contain" loading="lazy" />
            </div>
            {(meta?.url || screenshot.pageUrl) && (
              <p className="text-[11px] text-muted-foreground truncate mt-1 px-1">{meta?.url || screenshot.pageUrl}</p>
            )}
          </div>

          {/* Reasoning */}
          <div className="flex-1 min-w-0 space-y-3">
            {meta?.thinking && (
              <div className="flex gap-2">
                <Brain className="h-4 w-4 text-purple-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[10px] font-semibold text-purple-600 uppercase tracking-wider mb-0.5">Thinking</p>
                  <p className="text-sm text-foreground leading-relaxed">{meta.thinking}</p>
                </div>
              </div>
            )}
            {meta && meta.actions.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider ml-6">Actions</p>
                {meta.actions.map((action, ai) => {
                  const Icon = ACTION_ICONS[action.action] || MousePointer;
                  return (
                    <div key={ai} className="flex items-start gap-2 ml-1">
                      <div className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded',
                        action.action === 'done' ? 'bg-green-100' :
                        action.action === 'click' ? 'bg-blue-100' :
                        action.action === 'navigate' ? 'bg-amber-100' :
                        'bg-muted',
                      )}>
                        <Icon className={cn(
                          'h-3 w-3',
                          action.action === 'done' ? 'text-green-600' :
                          action.action === 'click' ? 'text-blue-600' :
                          action.action === 'navigate' ? 'text-amber-600' :
                          'text-muted-foreground',
                        )} />
                      </div>
                      <div className="min-w-0">
                        <span className="text-xs font-medium">{action.action}</span>
                        {action.description && (
                          <span className="text-xs text-muted-foreground ml-1.5">— {action.description}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
